/**
 * CreatorInfo — Displays creator wallet, fee split breakdown, creation date,
 * and contract explorer link.
 *
 * Story 4.2 — Task 4
 */

import { useCallback } from "react";

import { copyToClipboard } from "../../lib/clipboard";
import { cn } from "../../lib/cn";
import { formatAddress } from "../../lib/format";
import { formatRelativeTime } from "../../lib/format-number";
import { getBlockExplorerUrl } from "../../lib/wagmi-config";
import { useToast } from "../ui/Toast";

type CreatorInfoProps = {
	creatorWallet: string;
	feeSplit: {
		creator: number;
		treasury: number;
		protocol: number;
	};
	contractAddress: string;
	createdAt: string;
};

type FeeBarProps = {
	label: string;
	percentage: number;
	colorClass: string;
};

function FeeBar({ label, percentage, colorClass }: FeeBarProps) {
	return (
		<div className="flex items-center gap-2">
			<span className="text-xs text-pastel-gray w-16 shrink-0">{label}</span>
			<div className="flex-1 h-2 bg-pastel-light-gray rounded-full overflow-hidden">
				<div
					className={cn("h-full rounded-full", colorClass)}
					style={{ width: `${String(percentage)}%` }}
				/>
			</div>
			<span className="text-xs font-medium text-pastel-black w-10 text-right">{percentage}%</span>
		</div>
	);
}

function CopyButton({ text, label }: { text: string; label: string }) {
	const { addToast } = useToast();

	const handleCopy = useCallback(async () => {
		try {
			await copyToClipboard(text);
			addToast({ type: "success", title: `${label} copied!` });
		} catch {
			addToast({ type: "error", title: "Failed to copy" });
		}
	}, [text, label, addToast]);

	return (
		<button
			type="button"
			onClick={handleCopy}
			className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-pastel-light-gray transition-colors"
			title={`Copy ${label.toLowerCase()}`}
			aria-label={`Copy ${label.toLowerCase()}`}
		>
			<svg
				className="h-3.5 w-3.5 text-pastel-gray"
				viewBox="0 0 20 20"
				fill="currentColor"
				aria-hidden="true"
			>
				<path d="M7 3.5A1.5 1.5 0 018.5 2h3.879a1.5 1.5 0 011.06.44l3.122 3.12A1.5 1.5 0 0117 6.622V12.5a1.5 1.5 0 01-1.5 1.5h-1v-3.379a3 3 0 00-.879-2.121L10.5 5.379A3 3 0 008.379 4.5H7v-1z" />
				<path d="M4.5 6A1.5 1.5 0 003 7.5v9A1.5 1.5 0 004.5 18h7a1.5 1.5 0 001.5-1.5v-5.879a1.5 1.5 0 00-.44-1.06L9.44 6.439A1.5 1.5 0 008.378 6H4.5z" />
			</svg>
		</button>
	);
}

export function CreatorInfo({
	creatorWallet,
	feeSplit,
	contractAddress,
	createdAt,
}: CreatorInfoProps) {
	const explorerUrl = `${getBlockExplorerUrl()}/address/${contractAddress}`;

	return (
		<div className="rounded-xl border border-pastel-border bg-white p-5">
			<h3 className="text-sm font-semibold text-pastel-black mb-4">Token Info</h3>

			<div className="space-y-4">
				<div>
					<p className="text-xs text-pastel-gray mb-1">Creator</p>
					<div className="flex items-center gap-1">
						<span className="text-sm font-mono text-pastel-black">
							{formatAddress(creatorWallet)}
						</span>
						<CopyButton text={creatorWallet} label="Address" />
					</div>
				</div>

				<div>
					<p className="text-xs text-pastel-gray mb-1">Contract</p>
					<div className="flex items-center gap-1">
						<span className="text-sm font-mono text-pastel-black">
							{formatAddress(contractAddress)}
						</span>
						<CopyButton text={contractAddress} label="Address" />
						<a
							href={explorerUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-pastel-light-gray transition-colors"
							title="View on Explorer"
						>
							<span className="sr-only">View on block explorer</span>
							<svg
								className="h-3.5 w-3.5 text-pastel-gray"
								viewBox="0 0 20 20"
								fill="currentColor"
								aria-hidden="true"
							>
								<path
									fillRule="evenodd"
									d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5z"
									clipRule="evenodd"
								/>
								<path
									fillRule="evenodd"
									d="M6.194 12.753a.75.75 0 001.06.053L16.5 4.44v2.81a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.553l-9.056 8.194a.75.75 0 00-.053 1.06z"
									clipRule="evenodd"
								/>
							</svg>
						</a>
					</div>
				</div>

				<div>
					<p className="text-xs text-pastel-gray mb-2">Fee Split</p>
					<div className="space-y-2">
						<FeeBar
							label="Creator"
							percentage={feeSplit.creator}
							colorClass="bg-pastel-vibrant-blue"
						/>
						<FeeBar
							label="Treasury"
							percentage={feeSplit.treasury}
							colorClass="bg-pastel-vibrant-green"
						/>
						<FeeBar label="Protocol" percentage={feeSplit.protocol} colorClass="bg-purple-400" />
					</div>
				</div>

				<div>
					<p className="text-xs text-pastel-gray mb-1">Created</p>
					<p className="text-sm text-pastel-black" title={new Date(createdAt).toLocaleString()}>
						{formatRelativeTime(createdAt)}
					</p>
				</div>
			</div>
		</div>
	);
}

export function CreatorInfoSkeleton() {
	return (
		<div className="rounded-xl border border-pastel-border bg-white p-5 animate-pulse">
			<div className="h-4 w-24 bg-pastel-light-gray rounded mb-4" />
			<div className="space-y-4">
				<div>
					<div className="h-3 w-12 bg-pastel-light-gray rounded mb-2" />
					<div className="h-5 w-36 bg-pastel-light-gray rounded" />
				</div>
				<div>
					<div className="h-3 w-16 bg-pastel-light-gray rounded mb-2" />
					<div className="h-5 w-36 bg-pastel-light-gray rounded" />
				</div>
				<div>
					<div className="h-3 w-16 bg-pastel-light-gray rounded mb-2" />
					<div className="h-2 w-full bg-pastel-light-gray rounded mb-2" />
					<div className="h-2 w-full bg-pastel-light-gray rounded mb-2" />
					<div className="h-2 w-full bg-pastel-light-gray rounded" />
				</div>
			</div>
		</div>
	);
}
